// src/stores/payrollConfigStore.js
import { defineStore } from "pinia";
import axios from "axios";
import { toast } from "@/utils/toast";
import { buildHeaders } from "@/services/_headers.util";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

export const usePayrollConfigStore = defineStore("payrollConfig", {
  state: () => ({
    config: null, // the PayrollConfig model from backend
    companyCountry: "KE",
    companyCurrency: "KES",
    taxYear: new Date().getFullYear(),
    loading: false,
    error: null,
  }),
  actions: {
    async fetchConfig(year = null) {
      if (!(await guardPlanModule(MODULE_KEYS.HRM_PAYROLL))) {
        this.config = null;
        return;
      }

      if (this.loading) return;
      this.loading = true;
      this.error = null;

      try {
        const { data } = await axios.get(
          `${process.env.VUE_APP_API_URL}payroll/config`,
          {
            params: year ? { year } : {},
            headers: buildHeaders(),
          }
        );

        this.companyCountry = data.company_country || "KE";
        this.companyCurrency = data.company_currency || "KES";
        this.taxYear = data.year || new Date().getFullYear();
        this.config = data.config;
      } catch (err) {
        this.error =
          err?.response?.data?.message ||
          err.message ||
          "Failed to load payroll config";
        toast(this.error, "error");
      } finally {
        this.loading = false;
      }
    },

    /**
     * Save company-level overrides.
     * Pass only the pieces of "rules" you want to override.
     */
    async saveConfig(partialRules) {
      if (this.loading) return;
      this.loading = true;
      this.error = null;

      try {
        const year = this.taxYear || new Date().getFullYear();
        const { data } = await axios.put(
          `${process.env.VUE_APP_API_URL}payroll/config`,
          {
            tax_year: year,
            rules: partialRules,
          },
          { headers: buildHeaders() }
        );

        this.config = data.config;
        this.companyCountry = data.company_country || "KE";
        this.companyCurrency = data.company_currency || "KES";
        this.taxYear = data.year || new Date().getFullYear();

        toast("Configuration updated.", "success");
      } catch (err) {
        this.error =
          err?.response?.data?.message ||
          err.message ||
          "Failed to save  config";
        toast(this.error, "error");
        throw err;
      } finally {
        this.loading = false;
      }
    },
  },
});
